import React, { FC } from 'react';
import styled from '@emotion/styled';
import { useSplitPayment, useCurrency, useI18n } from '~Hooks';
import { Payment } from '~reducers';
import { Box } from '~Styles/box';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';

const PAYMENT_COLORS: { [key: string]: string } = {
  COMPLETED: 'var(--colorUIGreen)',
  REFUNDED: 'var(--colorUIYellow)',
  FAILED: 'var(--colorUIRed)',
  PENDING: 'var(--colorUIBlue)',
  REGULAR: 'var(--colorWhite)',
};

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
`;

const PaymentRow = styled.div<{ status?: string }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-left: 5px;
  border-left-width: 5px;
  border-left-style: solid;
  border-left-color: ${({ status = 'COMPLETED' }) => PAYMENT_COLORS[status]};
`;

interface Props {
  orderId: string;
  isRefunding: boolean;
  total: number;
  amountRemaining: number;
}

const PastPayments: FC<Props> = ({ isRefunding, orderId, total, amountRemaining }) => {
  const { i18n } = useI18n();
  const splitPayment = useSplitPayment(orderId);
  const currency = useCurrency();

  const renderPayment = (payment: Payment, index: number) => {
    const isLatest = splitPayment.latestPOSPayment?.id === payment.id;
    const status = payment.refunded
      ? 'REFUNDED'
      : isLatest
      ? splitPayment.latestPOSPayment?.status
      : 'COMPLETED';

    return (
      <PaymentRow status={status}>
        <Paragraph level={4} color={PAYMENT_COLORS[status || 'REGULAR']} bottom={0}>
          {index + 1}: {payment.methodTitle}
        </Paragraph>
        <Heading level={4} color={PAYMENT_COLORS[status || 'REGULAR']} bottom={0}>
          {currency.format(payment.amount)}
        </Heading>
      </PaymentRow>
    );
  };

  const paymentsStarted =
    splitPayment.splitOrder?.payments && splitPayment.splitOrder?.payments.length > 0;

  return (
    <Box display="flex" flexDirection="column" gap="small">
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Paragraph level={4} color={PAYMENT_COLORS['REGULAR']}>
          {i18n('SplitPayment.OriginalTotal')}:
        </Paragraph>
        <Heading level={4} color={PAYMENT_COLORS['REGULAR']}>
          {currency.format(total)}
        </Heading>
      </Box>

      {total !== amountRemaining && (
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Paragraph level={4} color={PAYMENT_COLORS['REGULAR']}>
            {i18n('SplitPayment.Remaining')}:
          </Paragraph>{' '}
          <Heading level={4} color={PAYMENT_COLORS['REGULAR']}>
            {currency.format(amountRemaining)}
          </Heading>
        </Box>
      )}

      {paymentsStarted && (
        <Wrapper display="flex" flexDirection="column" gap="small" padding="medium">
          {splitPayment.splitOrder?.payments.map(renderPayment)}
        </Wrapper>
      )}

      <Box display="flex" justifyContent="center" marginTop="xs">
        <Paragraph level={4} color="var(--colorBlueGray400)" bottom={0}>
          {paymentsStarted ? i18n('SplitPayment.Desc.Next') : i18n('SplitPayment.Desc')}:
        </Paragraph>
      </Box>
    </Box>
  );
};

export default PastPayments;
